/**
 * Shared primitives for the widget's canvas-based PNG exports (map, charts).
 *
 * Canvas needs literal values — the widget theme's `var(--...)` tokens are
 * silently ignored by fillStyle/font assignment — so exporters resolve the
 * custom properties from the live DOM (the host page sets them), falling
 * back to the widget's defaults when a variable isn't defined.
 */

import { t } from '../i18n';

/** Source credit rendered onto every exported image (map and charts). A
    function, not a constant: the language is set after this module loads. */
export function sourceText(): string {
  return t().exports.source;
}

export interface ExportTokens {
  sansStack: string;
  serifStack: string;
  textPrimary: string;
  textSecondary: string;
  surface: string;
  /** Accent used for selection highlights (--color-data). */
  dataColor: string;
}

export function resolveExportTokens(el: HTMLElement): ExportTokens {
  const styles = getComputedStyle(el);
  const cssVar = (name: string, fallback: string): string =>
    styles.getPropertyValue(name).trim() || fallback;
  return {
    sansStack: cssVar('--sans-stack', "-apple-system, 'Segoe UI', Roboto, sans-serif"),
    serifStack: cssVar('--serif-stack', 'Georgia, "Times New Roman", serif'),
    textPrimary: cssVar('--color-text-default', '#181C44'),
    textSecondary: cssVar('--color-text-muted', '#5A5E78'),
    surface: cssVar('--color-bg-surface', '#FFFFFF'),
    dataColor: cssVar('--color-data', '#181C44'),
  };
}

export function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Encodes the canvas as PNG and triggers a download named
 * `<filenameBase>_<YYYY-MM-DD>.png`. Rejects if encoding fails — including
 * the SecurityError a tainted canvas throws (image drawn without CORS).
 */
export function downloadCanvasPng(canvas: HTMLCanvasElement, filenameBase: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    try {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('PNG encoding failed'));
          return;
        }
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = `${filenameBase}_${dateStamp()}.png`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        resolve();
      }, 'image/png');
    } catch (err) {
      reject(err);
    }
  });
}

function dateStamp(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
